import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import _isEmpty from 'lodash/isEmpty';
import _reduce from 'lodash/reduce';
import _flatMap from 'lodash/flatMap';
import { checkUserFlag, setUserSetting } from '@zola-helpers/client/dist/es/util/userSettings';
import { isDesktop } from '@zola-helpers/client/dist/es/util/responsive';
import { getUserPlatformStats } from '@zola-helpers/client/dist/es/api/userPlatformStats';
import { filterHasUsedMobileApp } from '@zola-helpers/client/dist/es/api/userPlatformStats/responseHelpers';
import {
  sendMarketingSms,
  WMessageUserRequestSource,
} from '@zola-helpers/client/dist/es/api/marketing/marketing';

// Components
import AppDownloadDesktopModal from '@zola/zola-ui/src/components/Modal/AppDownloadDesktop';
import { GuestListRsvpsDeepLinkQR } from '@zola/zola-ui/src/assets/images/qr_deeplinks';
import RsvpsList from 'components/manage/website/entities/Rsvp/RsvpsList';
import GotItTooltip from 'components/manage/website/entities/Rsvp/ToggleTooltip';
import EditPageHeader from 'components/manage/website/entities/EditPageCommon/EditPageHeader';
import EditPageAddEntity from 'components/manage/website/entities/EditPageCommon/EditPageAddEntity';
import EditPageForm from 'components/manage/website/entities/EditPageCommon/EditPageFormContainer';

// Actions
import { showModal } from 'actions/ModalActions';
import { togglePage } from 'actions/website/PageActions';
import { moveEntity } from 'actions/website/EntityActions';
import { editEvent } from 'actions/website/entities/RsvpActions';
import { updateEventV2 } from 'actions/website/entities/EventActions';

// Selectors
import { getUserContext } from 'selectors/user/userSelectors';

import modalAsset from '../../../../../assets/images/website/rsvp/rsvp_notification.png';
// Util
import experimentFlags, { DESKTOP_TO_APP_ENABLED } from '../../../../../util/experimentFlags';

const RsvpEdit = ({
  guests,
  page,
  isPageHidden,
  themeKey,
  orderedIds,
  rsvpEntities,
  openEventFormModal,
  showErrorModal,
  showSuccessModal,
  onPageToggle,
  isGotItTooltip,
  closeGotItTooltip,
  onSortEnd,
  onEditEvent,
  onEditVirtualEvent,
  updateEvent,
  userContext,
}) => {
  const [desktopToAppModalVisible, setShowDesktopToAppModal] = useState(false);
  const [allowModal, setAllowModal] = useState(null);

  useEffect(() => {
    // Show modal if desktop and flag is on
    const desktopToAppOn = experimentFlags.getFlag(DESKTOP_TO_APP_ENABLED, userContext);
    if (isDesktop() && desktopToAppOn) {
      const userHasDownloadedAppPromise = getUserPlatformStats().then(filterHasUsedMobileApp);
      const hasSeenModalPromise = checkUserFlag('WebsiteEditRSVPPageAppModal');
      Promise.all([userHasDownloadedAppPromise, hasSeenModalPromise])
        .then(([hasDownloadedApp, hasSeenModal]) => {
          setAllowModal(!hasSeenModal && !hasDownloadedApp);
        })
        .catch(() => {
          // Something when wrong, just don't show the modal.
          setAllowModal(false);
        });
    } else {
      setAllowModal(false);
    }
  }, [userContext]);

  const checkToggleAllowed = pageId => {
    const noEvents = orderedIds.length === 0;
    const noGuests = _isEmpty(guests.byId);
    const numGuestsInvited = _reduce(
      guests.orderedIds,
      (sum, guestGroupId) => {
        const guestGroup = guests.byId[guestGroupId];
        const eventInvitations = _flatMap(guestGroup.guests, guest => guest.event_invitations);
        return sum + eventInvitations.length;
      },
      0
    );
    const noGuestsInvited = numGuestsInvited === 0;
    const rsvpDisabled =
      orderedIds.filter(id => rsvpEntities[id].collect_rsvps === true).length === 0;

    if (noEvents) {
      const errorModalProps = {
        heading: 'Want guests to RSVP on your wedding website?',
        body:
          "First, create an event and build a guest list for it. Then you'll be able to unhide this page and let guests RSVP on this page",
        button: {
          text: 'Create An Event',
          action: openEventFormModal,
        },
      };
      showErrorModal(errorModalProps);
    } else if (noGuests) {
      const errorModalProps = {
        heading: 'Want guests to start using this page?',
        body: 'First, build your Guest Directory.',
        button: {
          text: 'Go To Guest Directory',
          action: () => browserHistory.push('/wedding/manage/guests/all'),
        },
      };
      showErrorModal(errorModalProps);
    } else if (noGuestsInvited) {
      const errorModalProps = {
        heading: 'Want guests to start using this page?',
        body: 'First, choose some to invite.',
        button: {
          text: 'Invite Guests',
          action: () => browserHistory.push('/wedding/manage/guests/build'),
        },
      };
      showErrorModal(errorModalProps);
    } else if (rsvpDisabled) {
      const errorModalProps = {
        heading: 'Want guests to start using this page?',
        body:
          'Turn on online RSVPs for at least one of your events (and make sure guests have already been invited).',
      };
      showErrorModal(errorModalProps);
    } else {
      onPageToggle(pageId);

      if (isDesktop()) {
        if (allowModal) {
          setShowDesktopToAppModal(true);
          setUserSetting('WebsiteEditRSVPPageAppModal');
        } else {
          showSuccessModal();
        }
      }
    }
  };

  const handlePageToggle = pageId => {
    if (!isPageHidden) {
      // The page is currently visible
      onPageToggle(pageId);
    } else {
      checkToggleAllowed(pageId);
    }
  };

  const onEdit = event => {
    if (event.virtual_meeting) {
      // open edit virtual event modal
      onEditVirtualEvent(event);
    } else {
      // open normal edit event modal
      onEditEvent(event);
    }
  };

  const onModalClose = () => {
    setAllowModal(false);
    setShowDesktopToAppModal(false);
  };

  return (
    <div>
      {desktopToAppModalVisible && (
        <AppDownloadDesktopModal
          onClose={onModalClose}
          rootId="root"
          triggerRef={null}
          qrCode={GuestListRsvpsDeepLinkQR}
          hed="Did you know managing guests is easier on our app? Track RSVPs as they roll in!"
          imgSrc={modalAsset}
          imgAlt="Hot off the press: your guests RSVP'd."
          onSubmit={({ mobileNumber }) =>
            sendMarketingSms({
              phone_number: mobileNumber,
              source: WMessageUserRequestSource.EDIT_WEBSITE_RSVP,
            }).catch(() => undefined)
          }
        />
      )}
      <EditPageHeader
        page={page}
        themeKey={themeKey}
        onAdd={openEventFormModal}
        onPageToggle={handlePageToggle}
        entityName="Event"
        isGotItTooltip={isGotItTooltip}
        closeGotItTooltip={closeGotItTooltip}
        gotItTooltip={GotItTooltip({ closeTooltip: closeGotItTooltip })}
      />

      <>
        <EditPageForm page={page} />
        <EditPageAddEntity entityName="Event" onAdd={openEventFormModal} onAddText="Add An Event">
          {rsvpEntities && (
            <RsvpsList
              rsvpEntities={rsvpEntities}
              orderedIds={orderedIds}
              onSortEnd={onSortEnd}
              onEdit={onEdit}
              updateEvent={updateEvent}
              useDragHandle
              lockAxis="y"
            />
          )}
        </EditPageAddEntity>
      </>
    </div>
  );
};

const mapStateToProps = state => ({
  guests: state.guestList.guests,
  userContext: getUserContext(state),
});

const mapDispatchToProps = dispatch => ({
  onPageToggle: pageId => dispatch(togglePage(pageId)),
  showSuccessModal: () => dispatch(showModal('PUBLISH_RSVP', {}, { size: 'md' })),
  showErrorModal: errorProps =>
    dispatch(showModal('PUBLISH_PAGE_ERROR', { ...errorProps }, { size: 'md' })),
  onSortEnd: ({ oldIndex, newIndex }) => dispatch(moveEntity(oldIndex, newIndex)),
  onEditEvent: event => {
    dispatch(editEvent(event));
    dispatch(
      showModal('EVENT_FORM', { websiteEvent: true, formIdentifier: 'eventForm' }, { size: 'md' })
    );
  },
  onEditVirtualEvent: event => {
    dispatch(
      showModal(
        'VIRTUAL_EVENT_FORM',
        {
          websiteEvent: true,
          initialValues: event,
        },
        { size: 'md' }
      )
    );
  },
  updateEvent: event => dispatch(updateEventV2(event)),
});

RsvpEdit.propTypes = {
  page: PropTypes.shape({}),
  isPageHidden: PropTypes.bool,
  theme: PropTypes.shape({ key: PropTypes.string, font_name: PropTypes.string }),
  orderedIds: PropTypes.arrayOf(PropTypes.number),
  rsvpEntities: PropTypes.shape({}),
  openEventFormModal: PropTypes.func,
  showSuccessModal: PropTypes.func,
  onPageToggle: PropTypes.func,
  isGotItTooltip: PropTypes.bool,
  closeGotItTooltip: PropTypes.func,
  onSortEnd: PropTypes.func,
  onEditEvent: PropTypes.func,
  onEditVirtualEvent: PropTypes.func,
  updateEvent: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(RsvpEdit);
